import {
  APIDefaultHeaders,
  SDKEnviroment,
  APIMiddleware,
  HTTP_STATUS,
  APIParsedResponse,
} from '../../typings/APIRequester';

/**
 * @name Generic API requester
 * @description Used by both DeliverySDK and AtlasSDK
 * @version 5.7.8
 */
export default class APIRequester {
  private token: string = null;

  private enviroment: SDKEnviroment;

  private middlewares: APIMiddleware[] = [];

  public constructor({ token, enviroment = null }: APIDefaultHeaders) {
    this.token = token;
    this.enviroment = enviroment;
  }

  private static appendQueryParams(url: string, query: Record<string, any>): URL {
    const urlWithParams = new URL(url);
    const appendParam = (key: string): void => urlWithParams.searchParams.append(key, query[key]);

    if (query) {
      Object.keys(query).forEach(appendParam);
    }

    return urlWithParams;
  }

  protected static parseAPIURL(url: string, basePath: string): string {
    return url.replace('{basePath}', basePath);
  }

  protected async fetchAPI(
    url: string,
    method: string,
    params: any,
    headers: any = {},
    type?: string,
  ): Promise<any> {
    let finalUrl: URL = new URL(url);
    let paramsByMethod = {};
    const isSafeDealFileUpload = url.includes('/attachment');
    const isOptInRecommendation = url.includes('/opt-in/recommendations');
    const isDac7 = url.includes('dac7');
    // This variable is needed to call POST request with a query param. It will be properly handled:
    // https://git.naspersclassifieds.com/pay-and-ship/delivery/frontend/olxeu-a2b/-/issues/35
    const billingUserType = url.includes('me/addresses/billing') ? params?.userType : undefined;

    const finalHeaders = {
      Authorization: `Bearer ${this.token}`,
      'Content-Type': 'application/json',
      ...headers,
    };

    // for multipart/form-data requests Content-type header
    // should be automatically added by the browser
    if (isSafeDealFileUpload) {
      delete finalHeaders['Content-Type'];
    }

    if (method === 'GET' || method === 'HEAD') {
      finalUrl = APIRequester.appendQueryParams(url, params);
    } else if (billingUserType) {
      finalUrl = APIRequester.appendQueryParams(url, { userType: billingUserType });
      paramsByMethod = params ? { body: JSON.stringify({ data: { ...params.data, type } }) } : {};
    } else if (isSafeDealFileUpload) {
      const { file } = params;
      const formData = new FormData();
      formData.append('file', file);
      paramsByMethod = { body: formData };
    } else if (isOptInRecommendation) {
      paramsByMethod = params ? { body: JSON.stringify(params) } : {};
    } else if (isDac7) {
      paramsByMethod = params ? { body: JSON.stringify(params) } : {};
    } else if (params?.body) {
      paramsByMethod = params ? { body: JSON.stringify(params.body) } : {};
    } else {
      paramsByMethod = params ? { body: JSON.stringify({ data: { ...params.data, type } }) } : {};
    }

    if (this.enviroment) {
      finalHeaders.Enviroment = this.enviroment;
    }

    const getDefaultResponse = (response: Response): APIParsedResponse => ({
      body: null,
      status: response.status,
      error: !response.ok,
    });

    const caller = (): Promise<APIParsedResponse | Record<string, any>> =>
      fetch(finalUrl.toString(), {
        method,
        mode: 'cors',
        headers: finalHeaders,
        ...paramsByMethod,
      }).then((response): Record<string, any> => {
        let error = !response.ok;

        switch (response.status) {
        case 204 as HTTP_STATUS.NO_CONTENT:
          return getDefaultResponse(response);

        case 302 as HTTP_STATUS.FOUND:
          error = false;
          break;

        default:
          break;
        }

        const contentType = response.headers.get('content-type');
        const readableBody =
          contentType?.indexOf('application/json') !== -1 ? response.json() : response.text();

        return new Promise<APIParsedResponse>((resolve): void => {
          readableBody
            .then((body): void =>
              resolve({
                status: response.status,
                error,
                body,
              }),
            )
            .catch((): void => resolve(getDefaultResponse(response)));
        });
      });

    return caller().then(
      (res: APIParsedResponse): Promise<unknown> =>
        this.middlewares.reduce(
          (acc, curr): Promise<unknown> =>
            acc.then((arg: APIParsedResponse): any => curr(arg, caller)),
          Promise.resolve(res),
        ),
    );
  }

  public appendMiddleware(middleware: APIMiddleware): void {
    this.middlewares.push(middleware);
  }
}
